import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiUrl } from '../../config/api'
import './Premium.css'


export default function Premium({ onBack }) {
  const { session, user } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadBillingData()
  }, [])

  const loadBillingData = async () => {
    try {
      setLoading(true)

      if (session?.access_token) {
        try {
          // Load user profile for account_type
          const profileResponse = await fetch('https://riftchess-backend.fly.dev/api/v1/profile', {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          })

          if (profileResponse.ok) {
            const profileData = await profileResponse.json()
            setUserProfile(profileData)
          }

          // Load subscription status
          const subResponse = await fetch('https://riftchess-backend.fly.dev/api/v1/billing/subscription', {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          })

          if (subResponse.ok) {
            const subData = await subResponse.json()
            setSubscription(subData.subscription)
          }

          // Load payment history
          const historyResponse = await fetch('https://riftchess-backend.fly.dev/api/v1/billing/payment-history', {
            headers: {
              'Authorization': `Bearer ${session.access_token}`
            }
          })

          if (historyResponse.ok) {
            const historyData = await historyResponse.json()
            setPaymentHistory(historyData.payments || [])
          }
        } catch (apiError) {
          // Silently handle API errors since we're using external payment links
        }
      }
    } catch (error) {
      // Handle errors silently
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = (planType) => {
    const baseUrl = planType === 'monthly'
      ? 'https://pay.riftchess.com/b/7sY4gBca31hk8MbcxE6kg00'
      : 'https://pay.riftchess.com/b/cNi14p2zt7FI9Qf4186kg01'

    // Add prefilled email if user is logged in
    const userEmail = user?.email
    const paymentUrl = userEmail
      ? `${baseUrl}?prefilled_email=${encodeURIComponent(userEmail)}`
      : baseUrl

    window.open(paymentUrl, '_blank')
  }


  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.')) {
      return
    }

    try {
      const response = await fetch('https://riftchess-backend.fly.dev/api/v1/billing/subscription', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (response.ok) {
        loadBillingData()
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
    }
  }

  const handleReactivateSubscription = async () => {
    try {
      const response = await fetch('https://riftchess-backend.fly.dev/api/v1/billing/subscription/reactivate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (response.ok) {
        loadBillingData()
      }
    } catch (error) {
      console.error('Failed to reactivate subscription:', error)
    }
  }

  if (loading) {
    return (
      <div className="premium-container">
        <div className="premium-loading">Loading...</div>
      </div>
    )
  }

  return (
    <div className="premium-container">
      <div className="premium-content">
        <div className="premium-header">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h1>
            RiftChess Premium <span className="premium-star">★</span>
          </h1>
          <p className="premium-subtitle">
            Unlock advanced features and support RiftChess development
          </p>
        </div>


        {(subscription?.status === 'active' || user?.account_type === 'premium' || userProfile?.account_type === 'premium') ? (
          <div className="subscription-active-section">
            <div className="active-subscription-card">
              <div className="subscription-status">
                <h3>Premium Active <span className="premium-star">★</span></h3>
                {subscription ? (
                  <p className="status-display"><strong>Status:</strong> {subscription.status}</p>
                ) : (
                  <p className="status-display"><strong>Status:</strong> Premium Account Active</p>
                )}
              </div>
              <div className="subscription-details">
                {subscription ? (
                  <>
                    <p><strong>Plan:</strong> {subscription.plan_id} Premium</p>
                    <p><strong>Next billing:</strong> {new Date(subscription.current_period_end).toLocaleDateString()}</p>
                    {subscription.cancel_at_period_end && (
                      <p className="cancellation-notice">Will cancel at end of period</p>
                    )}
                  </>
                ) : (
                  <p>You have premium access to all features</p>
                )}
              </div>

              {subscription && (
                <div className="subscription-actions">
                  {subscription.cancel_at_period_end ? (
                    <button className="reactivate-btn" onClick={handleReactivateSubscription}>
                      Reactivate Subscription
                    </button>
                  ) : (
                    <button className="cancel-btn" onClick={handleCancelSubscription}>
                      Cancel Subscription
                    </button>
                  )}
                </div>
              )}
            </div>

            {paymentHistory.length > 0 && (
              <div className="payment-history-section">
                <h3>Payment History</h3>
                <div className="payment-history-list">
                  {paymentHistory.slice(0, 5).map((payment, index) => (
                    <div key={index} className="payment-item">
                      <div className="payment-details">
                        <span className="payment-description">{payment.description}</span>
                        <span className="payment-date">
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="payment-amount">
                        ${(payment.amount / 100).toFixed(2)} {payment.currency?.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="subscription-plans-section">
            <div className="premium-benefits-overview">
              <h2>Premium Benefits</h2>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <h4>Priority Matchmaking</h4>
                  <p>Get matched faster with premium queue priority</p>
                </div>
                <div className="benefit-item">
                  <h4>Cosmetic Drops</h4>
                  <p>Unlock exclusive cosmetic items and special effects</p>
                </div>
                <div className="benefit-item">
                  <h4>Exclusive Themes</h4>
                  <p>Access to premium board and piece themes</p>
                </div>
                <div className="benefit-item">
                  <h4>VIP Badge</h4>
                  <p>Show your support with a premium badge</p>
                </div>
                <div className="benefit-item">
                  <h4>Cloud Game Storage</h4>
                  <p>Save unlimited games to the cloud</p>
                </div>
                <div className="benefit-item">
                  <h4>Support Development</h4>
                  <p>Help us build the future of chess</p>
                </div>
              </div>
            </div>

            <div className="pricing-plans">
              <h2>Choose Your Plan</h2>
              <div className="plans-grid">
                <div className="plan-card">
                  <div className="plan-header">
                    <h3>Monthly Premium</h3>
                    <div className="plan-price">
                      <span className="price">$7</span>
                      <span className="period">/month</span>
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li>All premium benefits</li>
                    <li>Monthly billing</li>
                    <li>Cancel anytime</li>
                  </ul>
                  <button
                    className="subscribe-btn primary"
                    onClick={() => handleSubscribe('monthly')}
                  >
                    Subscribe Monthly
                  </button>
                </div>

                <div className="plan-card featured">
                  <div className="plan-badge">Best Value</div>
                  <div className="plan-header">
                    <h3>Annual Premium</h3>
                    <div className="plan-price">
                      <span className="price">$70</span>
                      <span className="period">/year</span>
                    </div>
                    <div className="savings">Save $14</div>
                  </div>
                  <ul className="plan-features">
                    <li>All premium benefits</li>
                    <li>2 months free</li>
                    <li>Cancel anytime</li>
                  </ul>
                  <button
                    className="subscribe-btn featured"
                    onClick={() => handleSubscribe('yearly')}
                  >
                    Subscribe Annually
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}